import React from "react";
import { cancelEvent, cancelRightClickEvent, isDocVisible, waitForTimer, waitUntilVisible } from "./utils";
import { displayIf } from "../utils/StyleUtils";
import { uid } from "../utils/MathUtils";
import { profileEnd, profileStart, timeEnd, timeStart } from "../utils/DebugUtils";

// Libs are initialized below in loadLibs and initAdobeAn
let AdobeAn;
let CreateJs;
const DEFAULT_FRAMERATE = 20;

// fork of https://github.com/bibixx/react-adobe-animate
// MIT license

class AnimateCC extends React.Component {
    static instances = {};

    static defaultProps = {
        composition: null,
        additionalSounds: [],
        frameWidth: NaN,
        frameHeight: NaN,
        scale: 1,
        shiftX: 0,
        shiftY: 0,
        style: {},
        changeFramerate: false,
        visible: true,
        onUnload: null,
    };

    constructor() {
        super();
        CreateJs = window.createjs;

        this.state = {
            error: false,
            id: uid(),
            top: 0,
            left: 0,
            width: 800,
            height: 600,
        };
    }

    timeStart(label) {
        timeStart(this.props.animationName + " -> " + label);
    }

    timeEnd(label) {
        timeEnd(this.props.animationName + " -> " + label);
    }

    profileStart(label) {
        profileStart(this.props.animationName + " -> " + label);
    }

    profileEnd(label) {
        profileEnd(this.props.animationName + " -> " + label);
    }

    componentDidMount() {
        (async () => {
            try {
                console.log(this.props.animationName, "did mount");
                AnimateCC.instances[this.props.animationName] = this;

                this.timeStart("load_js");
                await this.loadLib();
                this.timeEnd("load_js");

                this.initAdobeAn();
            } catch (e) {
                if (e.name === "AnimateCC") {
                    console.error(`AnimateCC: ${e.message}`);
                } else {
                    throw e;
                }

                this.setState({
                    error: true,
                });
            }
        })();
    }

    loadLib = () => {
        return new Promise((resolve) => {
            let basePath = this.props.base ? this.props.base : "";
            if (!basePath.endsWith("/")) basePath += "/";

            this.loader = new CreateJs.LoadQueue(true, basePath);
            let animationName = this.props.animationName;
            this.loader.name = animationName;
            this.loader.setMaxConnections(20);
            this.loader.installPlugin(CreateJs.Sound);

            const libraryLoaded = () => {
                this.loader.removeEventListener("fileload", libraryLoaded);
                resolve();
            };
            this.loader.addEventListener("fileload", libraryLoaded);

            this.loader.loadFile(`${animationName}.js`);
        });
    };

    initAdobeAn = () => {
        AdobeAn = window.AdobeAn;
        const { animationName } = this.props;

        const compositionId = this.getCompositionId(animationName);

        try {
            this.composition = AdobeAn.getComposition(compositionId);
            this.lib = this.composition.getLibrary();
        } catch (e) {
            if (e.message === "Cannot read property 'getLibrary' of undefined") {
                const err = new Error(`Animation with name ${animationName} was not found`);
                err.name = "AnimateCC";
                throw err;
            }
            throw e;
        }

        CreateJs.MotionGuidePlugin.install();

        this.loader.addEventListener("fileload", this.handleFileLoad);
        this.loader.addEventListener("complete", this.handleComplete);

        this.manifest = [...this.lib.properties.manifest, ...this.props.additionalSounds];

        this.checkManifestForDuplicates(this.manifest);

        this.timeStart("load_resources");

        this.loader.loadManifest(this.manifest);

        if (this.manifest.length === 0) {
            this.handleComplete();
        }
    };

    checkManifestForDuplicates = (manifest) => {
        for (let i = 0; i < manifest.length; i++) {
            for (let j = i + 1; j < manifest.length; j++) {
                if (manifest[i].src === manifest[j].src) {
                    console.warn("duplicate file in manifest", manifest[i].src);
                }
            }
        }
    };

    getCompositionId = (animationName) => {
        const { composition } = this.props;

        if (composition !== null) {
            return composition;
        }

        const compositionIds = Object.keys(AdobeAn.compositions);

        const [foundComposition] = compositionIds.filter((id) => {
            const lib = AdobeAn.compositions[id].getLibrary();
            const symbolNames = Object.keys(lib);

            const independent = symbolNames.filter((s) => {
                return !!(lib[s].prototype?.mode === "independent" && lib[s].prototype?.hasOwnProperty("actionFrames"));
            });

            return independent.filter((name) => name === animationName).length > 0;
        });

        return foundComposition;
    };

    handleFileLoad = (evt) => {
        if (evt && evt.item.type === "image") {
            const images = this.composition.getImages();
            images[evt.item.id] = evt.result;
        }
    };

    handleComplete = async (evt) => {
        this.timeEnd("load_resources");

        await waitForTimer(0);

        const { animationName } = this.props;

        if (evt) {
            this.timeStart("SpriteSheet");
            const ss = this.composition.getSpriteSheet();
            const queue = evt.target;
            const { ssMetadata } = this.lib;
            for (let i = 0; i < ssMetadata.length; i++) {
                ss[ssMetadata[i].name] = new CreateJs.SpriteSheet({
                    images: [queue.getResult(ssMetadata[i].name)],
                    frames: ssMetadata[i].frames,
                });
            }
            this.timeEnd("SpriteSheet");
        }

        const { properties } = this.lib;

        this.profileStart("create root instance");
        this.timeStart("create root instance");
        this.exportRoot = new this.lib[animationName]();
        this.exportRoot.framerate = properties.fps;
        this.exportRoot.lib = this.lib;
        this.timeEnd("create root instance");
        this.profileEnd("create root instance");

        this.timeStart("create stage");
        this.stage = new this.lib.Stage(this.canvas);
        this.stage.addEventListener("click", cancelRightClickEvent, true);
        this.stage.addEventListener("mousedown", cancelRightClickEvent, true);
        this.stage.addEventListener("pressup", cancelRightClickEvent, true);
        this.stage.addEventListener("pressmove", cancelRightClickEvent, true);
        this.timeEnd("create stage");

        AdobeAn.compositionLoaded(properties.id);

        this.props.changeFramerate && (CreateJs.Ticker.framerate = properties.fps);

        await waitUntilVisible();

        this.stage.addChild(this.exportRoot);
        this.startAnimation();
        this.resizeCanvas();

        document.addEventListener("visibilitychange", this.visibilityChange);
        window.addEventListener("resize", this.resizeCanvas);

        this.props.onLoaded && this.props.onLoaded(this.exportRoot);
    };

    // Code to support hidpi screens and responsive scaling.
    resizeCanvas = () => {
        if (!this.props.visible) return;

        if (this.props.print) {
            const innerScale = 1.43;
            const ratio = 1.3;
            const pRatio = 2;
            const w = 800;
            const h = 600;

            this.canvas.width = w * ratio * pRatio;
            this.canvas.height = h * ratio * pRatio;
            this.setState({
                top: 0,
                left: 0,
                width: w * ratio + "px",
                height: h * ratio + "px",
            });

            const scale = ratio * innerScale * pRatio;
            this.stage.setTransform(0, 0, scale, scale);
            return;
        }
        const { properties } = this.lib;
        const { frameWidth, frameHeight, shiftX, shiftY } = this.props;
        const w = isNaN(frameWidth) ? properties.width : frameWidth;
        const h = isNaN(frameHeight) ? properties.height : frameHeight;

        const iw = window.innerWidth;
        const ih = window.innerHeight;

        const pRatio = window.devicePixelRatio || 1;

        const ratio = Math.min(iw / w, ih / h);

        this.canvas.width = w * ratio * pRatio;
        this.canvas.height = h * ratio * pRatio;
        this.setState({
            top: (ih - h * ratio) / 2,
            left: (iw - w * ratio) / 2,
            width: w * ratio + "px",
            height: h * ratio + "px",
        });

        const scale = ratio * this.props.scale * pRatio;
        this.stage.setTransform(shiftX * scale, shiftY * scale, scale, scale);

        this.stage.tickOnUpdate = false;
        this.stage.update();
        this.stage.tickOnUpdate = true;
    };

    startAnimation = () => {
        if (this.props.print) return;
        console.log("start animation", this.props.animationName);
        this.stage.tickChildren = true;
        this.stage.removeEventListener("tickstart", cancelEvent);
        CreateJs.Ticker.addEventListener("tick", this.stage);
        this.stage.enableMouseOver();
    };

    stopAnimation = () => {
        if (this.props.print) return;
        console.log("stop animation", this.props.animationName);
        if (this.stage) {
            this.stage.tickChildren = false;
            this.stage.enableMouseOver(0);
            this.stage.addEventListener("tickstart", cancelEvent);
            CreateJs.Ticker.removeEventListener("tick", this.stage);
        }
    };

    visibilityChange = () => {
        if (this.props.print) return;
        if (this.props.visible && isDocVisible()) {
            this.startAnimation();
            this.resizeCanvas();
            this.canvas && this.canvas.focus();
        } else {
            this.stopAnimation();
        }
    };

    componentDidUpdate(prevProps, prevState, snapshot) {
        const propertyChanged = (name) => {
            if (isNaN(prevProps[name]) && isNaN(this.props[name])) {
                return false;
            }
            return prevProps[name] !== this.props[name];
        };

        if (propertyChanged("visible")) {
            this.visibilityChange();
        }

        if (["frameWidth", "frameHeight", "shiftX", "shiftY", "scale"].some(propertyChanged)) {
            this.resizeCanvas();
        }
    }

    componentWillUnmount() {
        console.log(this.props.animationName, "will unmount");
        this.props.onUnload && this.props.onUnload();

        delete AnimateCC.instances[this.props.animationName];

        this.loader.removeAll();
        this.loader.removeAllEventListeners();
        this.loader = null;

        this.props.changeFramerate && (CreateJs.Ticker.framerate = DEFAULT_FRAMERATE);

        window.removeEventListener("resize", this.resizeCanvas);
        document.removeEventListener("visibilitychange", this.visibilityChange);

        this.stopAnimation();

        // cleanup see https://github.com/CreateJS/EaselJS/issues/913
        if (this.stage) {
            this.stage.prevStage && (this.stage.prevStage.nextStage = null);
            this.stage.enableMouseOver(0);
            this.stage.enableDOMEvents(false);
            this.stage.removeAllEventListeners();
            this.stage.removeAllChildren();
            this.stage.canvas = null;
            this.stage = null;
        }
    }

    render() {
        return (
            <canvas
                id={this.props.animationName + "_" + this.state.id}
                ref={(el) => {
                    this.canvas = el;
                }}
                className={this.props.print ? "print" : ""}
                style={{
                    position: "absolute",
                    display: "block",
                    top: this.state.top,
                    left: this.state.left,
                    width: this.state.width,
                    height: this.state.height,
                    ...this.props.style,
                    ...displayIf(this.props.visible),
                }}
            />
        );
    }
}

window.ACC = AnimateCC.instances;

export default AnimateCC;
